import { ClubFooter } from './ClubFooter'
import { ClubHeader } from './ClubHeader'
import { Container } from './Container'
import { MemberNav } from './MemberNav'
import { ShareMasthead } from './ShareMasthead'
import { PRODUCT_LONG_NAME } from '../../lib/product'

/**
 * Which chrome a club screen wears.
 *
 *   * `public` — the marketing header (nav + Apply) and the full foot.
 *   * `clubhouse` — the member nav and the quiet member foot.
 *   * `share` — a PUBLIC TOKENED page: the club's mark, no nav, no CTA, and a
 *     one-line foot. See `ShareMasthead` for why it is its own chrome rather
 *     than `public` with the nav hidden.
 *   * `invite` — a PUBLIC TOKENED page with a CTA of its own (`/invites/:token`,
 *     `/join/quartermaster/:token`): the public header with its Apply pill
 *     withheld, over the full foot. The club spends one `flag` per page, and
 *     `public` would spend it on Apply for every invitee — who is signed out
 *     by definition — beside the page's own accept. Sign in stays, and a
 *     signed-in viewer still gets The Clubhouse. Unlike `share`, this chrome
 *     DOES read the session, so its screens render under an `AuthProvider`.
 */
type ClubChrome = 'public' | 'clubhouse' | 'share' | 'invite'

interface ClubPageProps {
  children: React.ReactNode
  chrome?: ClubChrome
}

/**
 * ⚠️ `share` is the one chrome that reads NO SESSION. Both `ClubHeader` and
 * `MemberNav` call `useAuth`, and so does `ClubFooter` (its last Membership
 * link follows the session) — which is why the share foot below is the club's
 * quiet foot rather than that one. A share page renders for a stranger holding
 * a link, with no `AuthProvider` above it at all, and `useAuth` THROWS without
 * a provider: anything wired in here that reads the session breaks the page
 * rather than degrading, and `sharedRound.test.tsx` renders exactly that way to
 * keep it so.
 */
const HEADS: Record<ClubChrome, React.ComponentType> = {
  public: ClubHeader,
  clubhouse: MemberNav,
  share: ShareMasthead,
  invite: InviteHead,
}

const FEET: Record<ClubChrome, React.ComponentType> = {
  public: ClubFooter,
  clubhouse: MemberFoot,
  share: ShareFoot,
  invite: ClubFooter,
}

/**
 * Page frame for every club screen: the skip link, chrome, the skip target,
 * and the foot.
 *
 * `#main` + tabIndex={-1} is the skip-link target the rest of the app already
 * uses; keeping it here means no screen has to remember it. The skip link that
 * points at it is the frame's first child under every chrome — it reads no
 * session, so `share` carries it too — sr-only until focused, and drawing no
 * ring of its own: focus is the ground's `:focus-visible` outline.
 */
export function ClubPage({ children, chrome = 'public' }: ClubPageProps) {
  const Head = HEADS[chrome]
  const Foot = FEET[chrome]

  return (
    <div className="flex min-h-screen flex-col">
      {/* Skip link (WCAG 2.4.1): first focusable on every club page —
          revealed on focus, jumps past the chrome to #main below. */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-club focus:border focus:border-brass/55 focus:bg-paper focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-pine"
      >
        Skip to main content
      </a>
      <Head />

      <main id="main" tabIndex={-1} className="flex-1 outline-none">
        {children}
      </main>

      <Foot />
    </div>
  )
}

/** The club's one-line foot: the mark on the left, what this page is on the right. */
function QuietFoot({ note }: { note: string }) {
  return (
    <footer className="mt-22 border-t border-brass/35 pb-8 pt-5.5">
      <Container className="flex flex-wrap justify-between gap-2 text-xs text-ink-soft">
        <span>© MMXXVI {PRODUCT_LONG_NAME}</span>
        <span>{note}</span>
      </Container>
    </footer>
  )
}

function MemberFoot() {
  return <QuietFoot note="Members only · In good standing" />
}

function ShareFoot() {
  return <QuietFoot note="Shared from the club’s book" />
}

/** The public header with its Apply pill withheld — the `invite` chrome's head. */
function InviteHead() {
  return <ClubHeader apply={false} />
}
